import { store } from '../state/store';
import { renderMarkdownToHtml, setHtmlPreservingFormPortals } from '../utils/markdown';
import { renderChart } from '../utils/chart-renderer';
import { isAutoScrollEnabled, scrollToBottom } from '../utils/scroll';
import { stripDisabledFormBlocks } from '../utils/form-renderer';
import type { AnalystProgress, AnalystProgressStep, ChatEventDispatcher, TurnStreamEvent } from '../types';
import type { IterationUsageMeta } from '../types/iteration-usage';

export interface StreamCallbacks {
  onTextDelta: (text: string, accumulated: string) => void;
  onToolCallStart: (id: string, name: string, input?: Record<string, unknown>) => void;
  onToolCallResult: (id: string, name: string, result: unknown, error: string | null) => void;
  onEvent?: (parsed: Record<string, unknown>) => void;
  onToolUse?: (id: string, name: string, input: Record<string, unknown>) => void;
  onFileDownload?: (data: { toolCallId: string; toolName: string; filename: string; url: string; contentType: string; size: number }) => void;
  onConversationId?: (conversationId: string) => void;
  onIterationUsage?: (
    iteration: number,
    totalTokens: number,
    tools: Array<{ toolName: string; success: boolean; argsSummary: string; resultSummary?: string }>,
    llmRequestPayload?: string | null,
    cacheReadTokens?: number,
    meta?: IterationUsageMeta,
  ) => void;
  onHistorySummarization?: (
    turnId: string,
    result:
      | { summaryCount: number; llmCallCount: number; totalTokens: number; narrativeSummary: string }
      | { failed: true; error: string },
  ) => void;
  onAnalystProgress?: (progress: AnalystProgress) => void;
  onConfirmationRequired: (token: string, toolName: string, toolArgs: Record<string, unknown>, ttlMs: number) => void;
  onDone: (accumulated: string, reason: string) => void;
  onError: (err: Error) => void;
}

/**
 * Extract text content from a parsed SSE JSON object.
 * Handles the server's structured format and common LLM formats.
 */
function extractText(parsed: Record<string, unknown>): string {
  const drivingKeys = [
    'drivingText',
    'driving_text',
    'ttsText',
    'tts_text',
    'voiceText',
    'voice_text',
    'spokenText',
    'spoken_text',
  ];

  const readDrivingField = (source: Record<string, unknown> | undefined): string => {
    if (!source) return '';
    for (const key of drivingKeys) {
      const value = source[key];
      if (typeof value === 'string' && value) {
        return value;
      }
    }
    return '';
  };

  // Server format: { type: "text_delta", data: { text: "..." } }
  if (parsed.type === 'text_delta') {
    const d = parsed.data as Record<string, unknown> | undefined;
    if (d && typeof d.text === 'string') return d.text;
    const drivingText = readDrivingField(d);
    if (drivingText) return drivingText;
  }

  // OpenAI: { choices: [{ delta: { content: "..." } }] }
  const choices = parsed.choices as Array<{ delta?: { content?: string } }> | undefined;
  if (choices?.[0]?.delta?.content) return choices[0].delta.content;

  // Anthropic: { delta: { text: "..." } }
  const delta = parsed.delta as { text?: string } | undefined;
  if (delta?.text) return delta.text;

  // Simple: { content: "..." }
  if (typeof parsed.content === 'string') return parsed.content;

  // Wrapped: { data: { content: "..." } } or { data: "..." }
  if (parsed.data) {
    if (typeof parsed.data === 'string') return parsed.data;
    const d = parsed.data as Record<string, unknown>;
    if (typeof d.content === 'string') return d.content;
    if (typeof d.text === 'string') return d.text;
    const drivingText = readDrivingField(d);
    if (drivingText) return drivingText;
    if (typeof d.message === 'string' && parsed.type !== 'error') return d.message;
  }

  // Plain: { text: "..." }
  if (typeof parsed.text === 'string') return parsed.text;

  // Message: { message: "..." }
  if (typeof parsed.message === 'string' && parsed.type !== 'error') return parsed.message;

  // Token: { token: "..." }
  if (typeof parsed.token === 'string') return parsed.token;

  const deltaRecord = parsed.delta && typeof parsed.delta === 'object'
    ? parsed.delta as Record<string, unknown>
    : undefined;
  const deltaDrivingText = readDrivingField(deltaRecord);
  if (deltaDrivingText) return deltaDrivingText;

  const rootDrivingText = readDrivingField(parsed);
  if (rootDrivingText) return rootDrivingText;

  return '';
}

/**
 * Directly update a message bubble's DOM content without triggering a full re-render.
 * Uses a .message-text div so appended elements (tool indicators, download cards) are preserved.
 */
export function updateMessageDOM(shadow: ShadowRoot, messageId: string, content: string, isStreaming: boolean): boolean {
  const el = shadow.querySelector(`[data-message-id="${messageId}"] .message-content`);
  if (!el) return false;
  const scrollContainer = shadow.querySelector('.panel-body') as HTMLDivElement | null;
  const shouldStickToBottom = isAutoScrollEnabled(scrollContainer);

  // Find or create the text container - keeps appended siblings (tool indicators, download cards) intact
  let textEl = el.querySelector('.message-text') as HTMLElement | null;
  if (!textEl) {
    textEl = document.createElement('div');
    textEl.className = 'message-text md-content';
    el.insertBefore(textEl, el.firstChild);
  }

  // Re-rendering the markdown happens on every text delta - preserve any already-portaled
  // form's live DOM (and the React portal mounted inside it) instead of wiping it out.
  const renderedContent = stripDisabledFormBlocks(content);
  setHtmlPreservingFormPortals(textEl, renderMarkdownToHtml(renderedContent, isStreaming));

  textEl
    .querySelectorAll('.chart-container:not(.chart-rendered):not(.chart-error)')
    .forEach((container) => renderChart(container as HTMLElement));

  // Auto-scroll only if user is near the bottom (not scrolled up to read history)
  if (shouldStickToBottom) {
    scrollToBottom(scrollContainer);
  }

  return true;
}

/**
 * Find all unrendered chart containers in a message and render them as SVG.
 * Called after streaming completes to activate chart placeholders.
 */
export function activateCharts(shadow: ShadowRoot, messageId?: string): void {
  const scope = messageId
    ? shadow.querySelector(`[data-message-id="${messageId}"] .message-content`)
    : shadow;
  if (!scope) return;

  const containers = scope.querySelectorAll('.chart-container:not(.chart-rendered):not(.chart-error)');
  containers.forEach(c => renderChart(c as HTMLElement));
}

const ANALYST_PROGRESS_STEPS: ReadonlySet<string> = new Set<AnalystProgressStep>(['started', 'thinking', 'querying', 'saving', 'fixing']);

function isAnalystProgressStep(value: unknown): value is AnalystProgressStep {
  return typeof value === 'string' && ANALYST_PROGRESS_STEPS.has(value);
}

/**
 * Process a single parsed SSE JSON event and dispatch to the right callback.
 * Returns the text extracted (if any) so the caller can accumulate it.
 */
export function processEvent(parsed: Record<string, unknown>, callbacks: StreamCallbacks, accumulated: string): string {
  const type = parsed.type as string | undefined;
  callbacks.onEvent?.(parsed);

  switch (type) {
    case 'text_delta': {
      const d = parsed.data as Record<string, unknown> | undefined;
      const text = d && typeof d.text === 'string' ? d.text : '';
      if (text) {
        const newAccumulated = accumulated + text;
        callbacks.onTextDelta(text, newAccumulated);
        return text;
      }
      return '';
    }

    case 'tool_call_start': {
      const d = parsed.data as Record<string, unknown> | undefined;
      if (d) {
        const id = (d.id as string) || '';
        const name = (d.name as string) || '';
        const input = d.input as Record<string, unknown> | undefined;
        callbacks.onToolCallStart(id, name, input);
      }
      return '';
    }

    case 'tool_call_result': {
      const d = parsed.data as Record<string, unknown> | undefined;
      if (d) {
        const id = (d.id as string) || '';
        const name = (d.name as string) || '';
        const result = d.result ?? null;
        const error = typeof d.error === 'string' ? d.error : null;
        callbacks.onToolCallResult(id, name, result, error);
      }
      return '';
    }

    case 'tool_use': {
      const d = parsed.data as Record<string, unknown> | undefined;
      if (d && callbacks.onToolUse) {
        const id = (d.id as string) || '';
        const name = (d.name as string) || '';
        const input = (d.input as Record<string, unknown>) || {};
        callbacks.onToolUse(id, name, input);
      }
      return '';
    }

    case 'file_download': {
      const d = parsed.data as Record<string, unknown> | undefined;
      if (d && callbacks.onFileDownload) {
        callbacks.onFileDownload({
          toolCallId: (d.toolCallId as string) || '',
          toolName: (d.toolName as string) || '',
          filename: (d.filename as string) || 'file',
          url: (d.url as string) || '',
          contentType: (d.contentType as string) || 'application/octet-stream',
          size: (d.size as number) || 0,
        });
      }
      return '';
    }

    case 'conversation_id': {
      const d = parsed.data as Record<string, unknown> | undefined;
      if (d && typeof d.conversationId === 'string' && callbacks.onConversationId) {
        callbacks.onConversationId(d.conversationId);
      }
      return '';
    }

    case 'iteration_usage': {
      const d = parsed.data as Record<string, unknown> | undefined;
      if (d && callbacks.onIterationUsage) {
        const iteration = typeof d.iteration === 'number' ? d.iteration : 0;
        const totalTokens = typeof d.totalTokens === 'number' ? d.totalTokens : 0;
        const tools = Array.isArray(d.tools)
          ? d.tools
            .filter((tool): tool is { toolName: string; success: boolean; argsSummary: string; resultSummary?: unknown } => (
              !!tool
              && typeof tool === 'object'
              && typeof (tool as { toolName?: unknown }).toolName === 'string'
              && typeof (tool as { success?: unknown }).success === 'boolean'
              && typeof (tool as { argsSummary?: unknown }).argsSummary === 'string'
            ))
            .map((tool) => ({
              ...tool,
              resultSummary: typeof tool.resultSummary === 'string' ? tool.resultSummary : undefined,
            }))
          : [];
        const llmRequestPayload = typeof d.llmRequestPayload === 'string' ? d.llmRequestPayload : null;
        const cacheReadTokens = typeof d.cacheReadTokens === 'number' ? d.cacheReadTokens : undefined;
        const meta: IterationUsageMeta = {
          turnId: typeof d.turnId === 'string' ? d.turnId : undefined,
          source: d.source === 'data_analyst' ? 'data_analyst' : 'main',
          parentIteration: typeof d.parentIteration === 'number' ? d.parentIteration : null,
          analystCallId: typeof d.analystCallId === 'string' ? d.analystCallId : null,
          resultName: typeof d.resultName === 'string' ? d.resultName : null,
          llmResponseText: typeof d.llmResponseText === 'string' ? d.llmResponseText : null,
        };
        // Analyst round 0 is the requirements check that reviews a Data_Analyst task before the analyst runs
        if ((iteration > 0 || meta.source === 'data_analyst') && totalTokens > 0) {
          callbacks.onIterationUsage(iteration, totalTokens, tools, llmRequestPayload, cacheReadTokens, meta);
        }
      }
      return '';
    }

    case 'history_summarization': {
      const d = parsed.data as Record<string, unknown> | undefined;
      if (d && typeof d.turnId === 'string' && callbacks.onHistorySummarization) {
        if (d.failed === true) {
          callbacks.onHistorySummarization(d.turnId, {
            failed: true,
            error: typeof d.error === 'string' ? d.error : 'unknown error',
          });
        } else {
          callbacks.onHistorySummarization(d.turnId, {
            summaryCount: typeof d.summaryCount === 'number' ? d.summaryCount : 0,
            llmCallCount: typeof d.llmCallCount === 'number' ? d.llmCallCount : 0,
            totalTokens: typeof d.totalTokens === 'number' ? d.totalTokens : 0,
            narrativeSummary: typeof d.narrativeSummary === 'string' ? d.narrativeSummary : '',
          });
        }
      }
      return '';
    }

    case 'analyst_progress': {
      const d = parsed.data as Record<string, unknown> | undefined;
      if (d && isAnalystProgressStep(d.step) && callbacks.onAnalystProgress) {
        callbacks.onAnalystProgress({ step: d.step, round: typeof d.round === 'number' ? d.round : 0 });
      }
      return '';
    }

    case 'confirmation_required': {
      const d = parsed.data as Record<string, unknown> | undefined;
      if (d) {
        const token = (d.confirmationToken as string) || '';
        const toolName = (d.toolName as string) || '';
        const toolArgs = (d.toolArgs as Record<string, unknown>) || {};
        const ttlMs = typeof d.ttlMs === 'number' ? d.ttlMs : 5 * 60 * 1000;
        callbacks.onConfirmationRequired(token, toolName, toolArgs, ttlMs);
      }
      return '';
    }

    case 'error': {
      const d = parsed.data as Record<string, unknown> | undefined;
      const errMsg = d?.message || parsed.message || 'Server error';
      const err = new Error(String(errMsg));
      // Attach full technical detail for event logging (not shown in chat)
      (err as any).detail = d?.detail || null;
      callbacks.onError(err);
      return '';
    }

    case 'done': {
      const d = parsed.data as Record<string, unknown> | undefined;
      const reason = (d?.reason as string) || 'complete';
      callbacks.onDone(accumulated, reason);
      return '';
    }

    default: {
      // Unknown type - try to extract text as fallback
      const text = extractText(parsed);
      if (text) {
        const newAccumulated = accumulated + text;
        callbacks.onTextDelta(text, newAccumulated);
        return text;
      }
      return '';
    }
  }
}

/**
 * Creates the dispatcher that feeds parsed events into `callbacks`. Events carrying a `seq` at or
 * below the last one seen are dropped, so the SSE reader and the polling fallback can both
 * deliver the same run's events without double-processing. Nothing is dispatched after the
 * stream finished ('done' / 'error').
 */
export function createEventDispatcher(callbacks: StreamCallbacks): ChatEventDispatcher {
  let accumulated = '';
  let lastSeq = 0;
  let finished = false;

  return {
    get accumulated() { return accumulated; },
    get lastSeq() { return lastSeq; },
    get finished() { return finished; },

    dispatch(parsed) {
      if (finished) return;
      if (typeof parsed.seq === 'number') {
        if (parsed.seq <= lastSeq) return;
        lastSeq = parsed.seq;
      }
      // processEvent runs onDone/onError synchronously - mark finished first so anything they
      // trigger (e.g. another dispatch) is already ignored.
      if (parsed.type === 'done' || parsed.type === 'error') finished = true;
      accumulated += processEvent(parsed, callbacks, accumulated);
    },

    dispatchRawText(text) {
      if (finished) return;
      accumulated += text;
      callbacks.onTextDelta(text, accumulated);
    },

    finishWithoutDoneEvent() {
      if (finished) return;
      finished = true;
      callbacks.onDone(accumulated, 'complete');
    },
  };
}

/**
 * Read an SSE stream and dispatch structured events via callbacks.
 *
 * `options.stopSignal` cancels reading without firing onDone/onError (the polling fallback took
 * over); `options.onActivity` fires on every received chunk, heartbeats included.
 */
export async function readStream(
  response: Response,
  callbacks: StreamCallbacks,
  options: { dispatcher?: ChatEventDispatcher; stopSignal?: AbortSignal; onActivity?: () => void } = {},
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) {
    callbacks.onError(new Error('Response body is not readable'));
    return;
  }

  const dispatcher = options.dispatcher ?? createEventDispatcher(callbacks);
  const { stopSignal } = options;
  const onStop = () => { reader.cancel().catch(() => { /* already closed */ }); };
  stopSignal?.addEventListener('abort', onStop, { once: true });

  const decoder = new TextDecoder();
  let buffer = '';
  let gotDoneMarker = false;

  const handleDataLine = (data: string) => {
    if (data === '[DONE]') {
      gotDoneMarker = true;
      return;
    }
    let parsed: TurnStreamEvent;
    try {
      parsed = JSON.parse(data);
    } catch {
      // Not JSON - treat entire data as plain text
      dispatcher.dispatchRawText(data);
      return;
    }
    dispatcher.dispatch(parsed);
  };

  try {
    while (!dispatcher.finished) {
      const { done, value } = await reader.read();
      if (done) break;
      options.onActivity?.();

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE lines
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith(':') || trimmed.startsWith('event:')) continue;
        if (trimmed.startsWith('data: ')) handleDataLine(trimmed.slice(6));
        if (dispatcher.finished) break;
      }
    }

    if (stopSignal?.aborted) return;

    // Process remaining buffer
    const remaining = decoder.decode();
    if (remaining) buffer += remaining;
    const trimmed = buffer.trim();
    if (trimmed.startsWith('data: ')) handleDataLine(trimmed.slice(6));

    // If server didn't send an explicit done event, fire it
    if (!gotDoneMarker) dispatcher.finishWithoutDoneEvent();
  } catch (err) {
    if (stopSignal?.aborted) return;
    callbacks.onError(err instanceof Error ? err : new Error(String(err)));
  } finally {
    stopSignal?.removeEventListener('abort', onStop);
    reader.releaseLock();
  }
}

/**
 * Simple streaming reader for backward compatibility.
 * Wraps readStream with basic text-only callbacks.
 */
export async function readStreamSimple(
  response: Response,
  messageId: string,
  shadow: ShadowRoot,
  onDone: (final: string) => void,
  onError: (err: Error) => void,
): Promise<void> {
  return readStream(response, {
    onConversationId: (id) => {
      store.setState({ conversationId: id });
    },
    onTextDelta: (_text, accumulated) => {
      updateMessageDOM(shadow, messageId, accumulated, true);
    },
    onToolCallStart: (id, name, input) => {
      store.addToolCall({ id, name, status: 'calling', inputs: input });
    },
    onToolCallResult: (id, _name, result, error) => {
      store.updateToolCall(id, {
        status: error ? 'error' : 'done',
        result: result ? JSON.stringify(result) : undefined,
        error: error || undefined,
      });
    },
    onConfirmationRequired: (_token, _toolName, _toolArgs, _ttlMs) => {
      // Not handled in simple mode
    },
    onDone: (accumulated) => {
      onDone(accumulated);
    },
    onError,
  });
}
